// import { FileUploadButton } from '@create-figma-plugin/ui';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from "@modulz/design-system";
import { useCallback, useState, useRef } from "preact/hooks";
import { highlight, languages } from "prismjs/components/prism-core";

import { VerticalSpace } from "../components/vertical-space";
import { Editor } from "../editor";
import { DatasetMap } from "../graphpad.types";

// import { ChevronDownIcon } from '@radix-ui/react-icons';
// import { styled } from '@stitches/react';
import { Fragment, h, FunctionComponent, JSX } from "preact";

// const AccordionChevron = styled(ChevronDownIcon, {
//   transition: "transform 300ms",
//   "[data-state=open] &": { transform: "rotate(90deg)" },
// });

export const MOCK_CSV = `name,count,country
Engineering,20,USA
Engineering,4,CAN
Design,15,CAN
Finance,12,FIN
`;

// Define a preact fileuploadbutton

type FileUploadButtonProps = {
  onChange: (files: File[]) => void;
  children: JSX.Element | string;
};

const FileUploadButton: FunctionComponent<FileUploadButtonProps> = ({
  onChange,
  children,
}) => {
  const buttonRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (event: Event) => {
      const input = event.target as HTMLInputElement;
      const files = input.files;
      if (files) {
        onChange(Array.from(files));
      }
    },
    [onChange]
  );

  return (
    <Fragment>
      <input
        ref={buttonRef}
        onChange={handleChange}
        type="file"
        style={{ display: "none" }}
        multiple={false}
      />
      <Button
        variant="blue"
        onClick={() => buttonRef.current && buttonRef.current.click()}
      >
        {children}
      </Button>
    </Fragment>
  );
};

export const DataDefinitionPanel = (props: {
  datasets: DatasetMap;
  onDatasetsChange: (d: DatasetMap) => void;
}) => {
  const { onDatasetsChange } = props;

  const [localDatasets, setLocalDatasets] = useState<DatasetMap>(
    props.datasets
  );

  const handleSaveDatasets = useCallback(
    (newText: DatasetMap) => {
      onDatasetsChange(newText);
    },
    [onDatasetsChange]
  );

  const handleAddDataset = useCallback(
    (mode: "csv" | "json", contents?: string, key?: string) => {
      const numberDatasets = Object.keys(localDatasets).length;
      const newDatasetName = key ? key : `table${numberDatasets + 1}`;

      let dataset =
        mode === "json"
          ? JSON.stringify([{ height: 3, width: 4 }], null, 2)
          : MOCK_CSV;

      if (contents !== undefined) {
        dataset = contents;
      }

      // Const newDatasetName = `table${numDatasets}`;
      setLocalDatasets({
        ...localDatasets,
        [newDatasetName]: dataset,
      });
    },
    [localDatasets]
  );

  return (
    <div>
      <VerticalSpace space="small" />

      <p>Click table name to view data.</p>

      <p>Data can be pasted from your clipboard, or uploaded from a file.</p>

      <p>When you're done editing, click "Save changes".</p>

      <VerticalSpace space="small" />

      <div>
        <Button variant="gray" onClick={() => handleAddDataset("csv")}>
          Add dataset (CSV)
        </Button>
        <span style={{ width: 8, marginRight: 8 }} />
        <Button
          variant="blue"
          onClick={() => {
            handleSaveDatasets(localDatasets);
          }}
        >
          Save changes
        </Button>
      </div>

      <VerticalSpace space="extraSmall" />

      <div style={{ width: 8, display: "inline-block" }} />

      <div style={{ width: 8, display: "inline-block" }} />

      {Object.entries(localDatasets).map(([key, value]) => {
        const onValueInputHandler = (value_: string) => {
          setLocalDatasets({
            ...localDatasets,
            [key]: value_,
          });
        };

        const isJsonDataset = value.trimStart().startsWith("[");

        return (
          <Fragment key={key}>
            <Accordion type="single">
              <AccordionItem value="default-1">
                <AccordionTrigger>
                  <div style={{ display: "flex", alignSelf: "start" }}>
                    {/* <AccordionChevron aria-hidden /> */}

                    <span style={{ marginLeft: 10 }}>{`${key} : ${
                      isJsonDataset ? "JSON" : "CSV"
                    }`}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <VerticalSpace space="extraSmall" />
                  <Button
                    variant="red"
                    onClick={() => {
                      const filteredData = Object.fromEntries(
                        Object.entries(localDatasets).filter(([k]) => k !== key)
                      );
                      setLocalDatasets(filteredData);
                    }}
                  >
                    {" "}
                    Delete
                  </Button>
                  <div style={{ width: 8, display: "inline-block" }} />

                  <FileUploadButton
                    onChange={(files) => {
                      if (files.length > 0) {
                        const file = files[0];
                        file.text().then((contents) => {
                          handleAddDataset("csv", contents, key);
                        });
                      }
                    }}
                  >
                    Replace with CSV
                  </FileUploadButton>
                  <div style={{ width: 8, display: "inline-block" }} />

                  {isJsonDataset && (
                    <Button
                      variant="gray"
                      onClick={() => {
                        try {
                          const base = {
                            ...localDatasets,
                            [key]: JSON.stringify(JSON.parse(value), null, 2),
                          };
                          setLocalDatasets(base);
                        } catch {
                          console.warn("could not parse");
                        }
                      }}
                    >
                      {" "}
                      Format
                    </Button>
                  )}
                  <VerticalSpace space="small" />

                  <Editor
                    value={value}
                    onValueChange={onValueInputHandler}
                    highlight={(code: string) => {
                      if (code.startsWith("[")) {
                        return highlight(code, languages.json);
                      }
                      return code;
                    }}
                    padding={10}
                    style={{
                      fontFamily: '"Fira code", "Fira Mono", monospace',
                      fontSize: 12,
                      backgroundColor: "rgb(235,235,235)"
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <VerticalSpace space="small" />
          </Fragment>
        );
      })}

      <VerticalSpace space="small" />
      <details>
        <summary>Notes on sample data</summary>
        <VerticalSpace space="small" />
        <p>
          To get you started, this widget is loaded with 2 sample datasets, 1
          CSV (Comma Separated Values) and 1 JSON (a format suited for nested
          data). Feel free to replace or delete. You can refer to them by name
          (table1, table2) in the "Define Graph" tab.
        </p>
      </details>

      <VerticalSpace space="medium" />
    </div>
  );
};
